import { useState, useEffect } from "react";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Button } from "@/components/ui/Button";
import { ToolPageLayout } from "@/components/ToolPageLayout";
import { RotateCcw } from "lucide-react";

export function RandomNumberGenerator() {
  const [min, setMin] = useState(1);
  const [max, setMax] = useState(100);
  const [res, setRes] = useState<number | null>(42);

  const gen = (valMin = min, valMax = max) => {
    const l = Number(valMin);
    const h = Number(valMax);
    if (isNaN(l) || isNaN(h) || l > h) return;
    setRes(Math.floor(Math.random() * (h - l + 1)) + l);
  };

  return (
    <ToolPageLayout
      title="Random Number Generator - Pick Numbers Online"
      description="Generate randomized numbers within custom bounds. Perfect for raffles, sweepstakes, giveaways, or security variables."
      h1="Random Number Generator"
      category="Math & Numbers"
      categoryPath="/number-tools"
      howToUse={[
        "Set the minimum boundary value (inclusive).",
        "Set the maximum boundary value (inclusive).",
        "Click Generate to choose a secure random value instantly."
      ]}
      faq={[
        { question: "Is this generator cryptographically secure?", answer: "This tool uses standard Math.random() entropy, which is perfect for non-financial giveaways and games." },
        { question: "Can I generate negative values?", answer: "Yes, you can set the minimum boundary to negative values." }
      ]}
    >
      <div className="space-y-4 max-w-sm mx-auto text-center bg-white border border-slate-200 p-6 rounded-2xl shadow-xs">
        <div className="grid grid-cols-2 gap-4 text-left">
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold text-slate-700">Minimum</Label>
            <Input type="number" value={min} onChange={e => { const v = Number(e.target.value); setMin(v); gen(v, max); }} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold text-slate-700">Maximum</Label>
            <Input type="number" value={max} onChange={e => { const v = Number(e.target.value); setMax(v); gen(min, v); }} />
          </div>
        </div>
        <Button onClick={() => gen()} className="w-full bg-slate-900 hover:bg-slate-800 text-white font-medium h-10">Generate Number</Button>
        {res !== null && (
          <div className="p-6 bg-slate-50 border border-slate-200 rounded-xl mt-4">
            <span className="text-[10px] text-slate-400 uppercase font-black tracking-widest block mb-1">Random Output</span>
            <div className="text-5xl font-black text-slate-850">{res}</div>
          </div>
        )}
      </div>
    </ToolPageLayout>
  );
}

export function GpaCalculator() {
  const [grades, setGrades] = useState([
    { grade: 4.0, credits: 3 },
    { grade: 3.0, credits: 4 },
    { grade: 3.7, credits: 3 }
  ]);
  
  const totalCredits = grades.reduce((acc, curr) => acc + curr.credits, 0);
  const gpa = totalCredits > 0 
    ? grades.reduce((acc, curr) => acc + curr.grade * curr.credits, 0) / totalCredits 
    : 0;

  return (
    <ToolPageLayout
      title="GPA Calculator Online - Semester Grade Point Average"
      description="Determine your overall academic GPA (Grade Point Average) based on custom grade points and credit weights."
      h1="GPA Calculator"
      category="Math & Numbers"
      categoryPath="/number-tools"
      howToUse={[
        "Add course items to the list.",
        "Set the numeric Grade score value (e.g. 4.0 for an A, 3.0 for a B).",
        "Input corresponding credit values (e.g. 3 credits) and read the live weighted overall GPA score."
      ]}
      faq={[
        { question: "What scale is used?", answer: "This is based on the standard US 4.0 grading scale." },
        { question: "How is the GPA weighted?", answer: "Each grade score is multiplied by the course credit count, summed up, and divided by total course credits." }
      ]}
    >
      <div className="space-y-4 max-w-md mx-auto bg-white border border-slate-200 p-6 rounded-2xl shadow-xs">
        <div className="space-y-3">
          {grades.map((g, i) => (
            <div key={i} className="flex gap-4 items-end border-b border-slate-50 pb-2">
              <div className="flex-1 space-y-1">
                <Label className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Grade (0.0 - 4.0)</Label>
                <Input type="number" step="0.1" value={g.grade} onChange={e => { const ng = [...grades]; ng[i].grade = Number(e.target.value); setGrades(ng); }} className="h-9" />
              </div>
              <div className="flex-1 space-y-1">
                <Label className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Credits</Label>
                <Input type="number" value={g.credits} onChange={e => { const ng = [...grades]; ng[i].credits = Number(e.target.value); setGrades(ng); }} className="h-9" />
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => { const ng = grades.filter((_, idx) => idx !== i); setGrades(ng); }}
                className="text-red-500 hover:text-red-600 hover:bg-red-50 h-9 px-2 text-xs shrink-0"
              >
                Delete
              </Button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setGrades([...grades, { grade: 4.0, credits: 3 }])} variant="outline" size="sm" className="w-full text-xs h-9">Add New Course</Button>
          <Button onClick={() => setGrades([{ grade: 4.0, credits: 3 }])} variant="ghost" size="sm" className="text-xs h-9"><RotateCcw className="w-3.5 h-3.5" /></Button>
        </div>
        <div className="p-4 bg-indigo-50 border border-indigo-100 rounded-xl text-center mt-4">
          <div className="text-[10px] uppercase tracking-widest text-indigo-500 font-bold mb-1">Overall GPA Score</div>
          <div className="text-4xl font-extrabold text-indigo-950">{isNaN(gpa) ? "0.00" : gpa.toFixed(2)}</div>
        </div>
      </div>
    </ToolPageLayout>
  );
}

export function BmiCalculator() {
  const [w, setW] = useState<number | "">(70);
  const [h, setH] = useState<number | "">(175);
  
  const hm = Number(h) / 100;
  let bmi = 0;
  if (w && h && Number(h) > 0) {
    bmi = Number(w) / (hm * hm);
  }

  let status = "";
  let statusColor = "text-slate-700";
  if (bmi > 0) {
    if (bmi < 18.5) { status = "Underweight"; statusColor = "text-amber-600"; }
    else if (bmi < 25) { status = "Normal weight"; statusColor = "text-emerald-600 font-bold"; }
    else if (bmi < 30) { status = "Overweight"; statusColor = "text-amber-600"; }
    else { status = "Obese"; statusColor = "text-red-600 font-bold"; }
  }

  return (
    <ToolPageLayout
      title="BMI Calculator Online - Body Mass Index"
      description="Check your Body Mass Index (BMI) based on weight and height metrics to evaluate body mass ranges."
      h1="BMI Calculator"
      category="Math & Numbers"
      categoryPath="/number-tools"
      howToUse={[
        "Input your weight in kilograms (kg).",
        "Input your height in centimeters (cm).",
        "See your computed Body Mass Index score and health weight status instantly."
      ]}
      faq={[
        { question: "What is a healthy BMI range?", answer: "According to the World Health Organization (WHO), a healthy BMI is between 18.5 and 24.9." },
        { question: "Are height and weight values private?", answer: "Yes, all data remains entirely inside your client browser." }
      ]}
    >
      <div className="space-y-4 max-w-sm mx-auto text-center bg-white border border-slate-200 p-6 rounded-2xl shadow-xs">
        <div className="space-y-1.5 text-left">
          <Label className="text-sm font-semibold text-slate-700">Weight (kg)</Label>
          <Input type="number" min="1" value={w} onChange={e => setW(e.target.value === "" ? "" : Number(e.target.value))} />
        </div>
        <div className="space-y-1.5 text-left">
          <Label className="text-sm font-semibold text-slate-700">Height (cm)</Label>
          <Input type="number" min="1" value={h} onChange={e => setH(e.target.value === "" ? "" : Number(e.target.value))} />
        </div>
        <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl mt-4">
          <span className="text-[10px] text-slate-400 uppercase font-bold tracking-widest block mb-1">Your BMI Score</span>
          <div className="text-4xl font-black text-slate-800 mb-1">{bmi > 0 ? bmi.toFixed(1) : "0.0"}</div>
          <div className={`text-sm font-semibold ${statusColor}`}>{status || "-"}</div>
        </div>
      </div>
    </ToolPageLayout>
  );
}

export function LoanCalculator() {
  const [p, setP] = useState<number | "">(250000); // Principal
  const [r, setR] = useState<number | "">(5.5); // Annual Interest Rate %
  const [t, setT] = useState<number | "">(30); // Years

  const valP = Number(p);
  const valR = Number(r);
  const valT = Number(t);

  const monthlyRate = valR / 100 / 12;
  const numPayments = valT * 12;
  
  let emi = 0;
  if (valP > 0 && valT > 0 && valR >= 0) {
    if (monthlyRate === 0) {
      emi = valP / numPayments;
    } else {
      emi = valP * monthlyRate * Math.pow(1 + monthlyRate, numPayments) / (Math.pow(1 + monthlyRate, numPayments) - 1);
    }
  }

  const totalPayment = emi * numPayments;
  const totalInterest = totalPayment > valP ? totalPayment - valP : 0;

  return (
    <ToolPageLayout
      title="Mortgage & Loan Calculator - Calculate Monthly EMI Payments"
      description="Estimate monthly principal and interest payments, total loan cost, and interest accruals easily using custom rates."
      h1="Loan Calculator"
      category="Math & Numbers"
      categoryPath="/number-tools"
      howToUse={[
        "Input your Loan Principal amount ($).",
        "Input the targeted annual interest rate (%).",
        "Set the total repayment timeline in years.",
        "Inspect the broken down Monthly EMI, total principal, and total accrued interest results."
      ]}
      faq={[
        { question: "What is an EMI?", answer: "Equated Monthly Installment is the fixed payment amount made by a borrower to a lender at a specified date each calendar month." },
        { question: "How does interest rate affect total cost?", answer: "Higher interest rates cause significantly larger overall interest accruals over long timelines." }
      ]}
    >
      <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto bg-white border border-slate-200 p-6 rounded-2xl shadow-xs">
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold text-slate-700">Loan Amount ($)</Label>
            <Input type="number" min="0" value={p} onChange={e => setP(e.target.value === "" ? "" : Number(e.target.value))} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold text-slate-700">Interest Rate (%)</Label>
            <Input type="number" min="0" step="0.1" value={r} onChange={e => setR(e.target.value === "" ? "" : Number(e.target.value))} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold text-slate-700">Loan Term (Years)</Label>
            <Input type="number" min="1" value={t} onChange={e => setT(e.target.value === "" ? "" : Number(e.target.value))} />
          </div>
        </div>
        <div className="space-y-4 flex flex-col justify-between">
          <div className="p-4 bg-indigo-50 border border-indigo-100 rounded-xl text-center">
            <div className="text-[10px] text-indigo-500 font-bold uppercase tracking-widest mb-1">Monthly Payment (EMI)</div>
            <div className="text-3xl font-black text-indigo-900">${emi.toFixed(2)}</div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl text-center">
              <div className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mb-1">Total Principal</div>
              <div className="text-base font-extrabold text-slate-750">${valP > 0 ? valP.toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2}) : "0.00"}</div>
            </div>
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl text-center">
              <div className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mb-1">Total Interest</div>
              <div className="text-base font-extrabold text-slate-750">${totalInterest.toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})}</div>
            </div>
          </div>
        </div>
      </div>
    </ToolPageLayout>
  );
}

export function NumberTools2() {
  return (
    <ToolPageLayout
      title="Number Tools"
      description="Various number calculators: Random number generator, GPA, BMI, and Loan calculators."
      h1="Number Tools"
      category="Number Tools"
      categoryPath="/number-tools"
      howToUse={[
        "Select the calculator you need.",
        "Enter your values.",
        "See the results."
      ]}
      faq={[
        { question: "Are these tools free?", answer: "Yes, all our number tools are free." }
      ]}
    >
      <div className="space-y-12">
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs"><h2 className="text-xl font-bold mb-4">Random Number Generator</h2><RandomNumberGenerator /></section>
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs"><h2 className="text-xl font-bold mb-4">GPA Calculator</h2><GpaCalculator /></section>
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs"><h2 className="text-xl font-bold mb-4">BMI Calculator</h2><BmiCalculator /></section>
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs"><h2 className="text-xl font-bold mb-4">Loan Calculator</h2><LoanCalculator /></section>
      </div>
    </ToolPageLayout>
  );
}
